import { useState, type ComponentPropsWithoutRef, type ReactNode } from "react";
import ReactMarkdown from "react-markdown";
import remarkGfm from "remark-gfm";
import { Link as LinkIcon } from "lucide-react";
import { cn } from "@/lib/cn";

/** Mirror of slugifyHeading() in scripts/sync-help-docs.mjs so TOC anchors line up. */
function slugify(text: string): string {
  return text
    .toLowerCase()
    .replace(/[^\w\s-]/g, "")
    .trim()
    .replace(/\s+/g, "-");
}

/** Flatten heading children to plain text for the anchor id. */
function textOf(node: ReactNode): string {
  if (node == null || node === false) return "";
  if (typeof node === "string" || typeof node === "number") return String(node);
  if (Array.isArray(node)) return node.map(textOf).join("");
  if (typeof node === "object" && "props" in node) {
    return textOf(
      (node as { props?: { children?: ReactNode } }).props?.children,
    );
  }
  return "";
}

function AnchoredHeading({
  level,
  className,
  children,
}: {
  level: 2 | 3;
  className: string;
  children: ReactNode;
}) {
  const id = slugify(textOf(children));
  const [copied, setCopied] = useState(false);
  const Tag = level === 2 ? "h2" : "h3";
  const copy = () => {
    const url = `${window.location.origin}${window.location.pathname}#${id}`;
    void navigator.clipboard?.writeText(url).then(() => {
      setCopied(true);
      window.setTimeout(() => setCopied(false), 1400);
    });
  };
  return (
    <Tag id={id} className={cn("group scroll-mt-24", className)}>
      <a href={`#${id}`} className="no-underline">
        {children}
      </a>
      <button
        type="button"
        onClick={copy}
        aria-label={copied ? "Link copied" : "Copy link to this section"}
        title={copied ? "Link copied" : "Copy link to this section"}
        className="ml-2 inline-flex translate-y-[-1px] items-center rounded p-0.5 align-middle text-ink-muted opacity-0 transition-opacity hover:text-accent-text focus-visible:opacity-100 focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring group-hover:opacity-100"
      >
        <LinkIcon className={cn("size-3.5", copied && "text-accent-text")} />
      </button>
    </Tag>
  );
}

/**
 * Render a help-article markdown body with the Tktic design system. Raw HTML is
 * NOT enabled (react-markdown's default), so article content can't inject markup.
 */
export function DocsMarkdown({ body }: { body: string }) {
  return (
    <div className="w-full text-[0.95rem] leading-relaxed text-ink">
      <ReactMarkdown
        skipHtml
        remarkPlugins={[remarkGfm]}
        components={{
          h1: ({ children }) => (
            <h1 className="mb-3 font-display text-2xl font-semibold tracking-tight text-ink">
              {children}
            </h1>
          ),
          h2: ({ children }) => (
            <AnchoredHeading
              level={2}
              className="mt-8 mb-2 font-display text-lg font-semibold tracking-tight text-ink"
            >
              {children}
            </AnchoredHeading>
          ),
          h3: ({ children }) => (
            <AnchoredHeading
              level={3}
              className="mt-5 mb-1.5 font-display text-base font-semibold text-ink"
            >
              {children}
            </AnchoredHeading>
          ),
          p: ({ children }) => (
            <p className="my-3 text-ink-muted">{children}</p>
          ),
          ul: ({ children }) => (
            <ul className="my-3 list-disc space-y-1.5 pl-5 text-ink-muted marker:text-ink-muted">
              {children}
            </ul>
          ),
          ol: ({ children }) => (
            <ol className="my-3 list-decimal space-y-1.5 pl-5 text-ink-muted marker:text-ink-muted">
              {children}
            </ol>
          ),
          li: ({ children }) => <li className="pl-1">{children}</li>,
          strong: ({ children }) => (
            <strong className="font-semibold text-ink">{children}</strong>
          ),
          code: ({ children }) => (
            <code className="rounded bg-elevated px-1.5 py-0.5 font-mono text-[0.85em] text-ink">
              {children}
            </code>
          ),
          a: ({ href, children }) => <DocLink href={href}>{children}</DocLink>,
          img: ({ src, alt }) => (
            <img
              src={typeof src === "string" ? src : undefined}
              alt={alt ?? ""}
              loading="lazy"
              className="my-4 w-full rounded border border-rule bg-surface shadow-sm"
            />
          ),
          blockquote: ({ children }) => (
            <blockquote className="my-4 rounded border-l-2 border-accent bg-elevated px-4 py-2 text-ink-muted">
              {children}
            </blockquote>
          ),
          table: ({ children }) => (
            <div className="my-4 overflow-x-auto rounded border border-rule bg-surface shadow-sm">
              <table className="w-full border-collapse text-left text-sm">
                {children}
              </table>
            </div>
          ),
          thead: ({ children }) => (
            <thead className="bg-elevated">{children}</thead>
          ),
          th: ({ children }) => (
            <th className="border-b border-rule px-3 py-2 font-display text-xs font-normal uppercase tracking-wide text-ink-muted">
              {children}
            </th>
          ),
          td: ({ children }) => (
            <td className="border-b border-rule/60 px-3 py-2 align-top text-ink-muted">
              {children}
            </td>
          ),
          hr: () => <hr className="my-6 border-rule" />,
        }}
      >
        {body}
      </ReactMarkdown>
    </div>
  );
}

/**
 * Cross-links in articles are relative `.md` paths (e.g. `goals.md`,
 * `live-streams.md`). Rewrite them to in-app `/docs/<slug>` links so they
 * navigate within the SPA. External and anchor links pass through.
 */
function DocLink({
  href,
  children,
}: ComponentPropsWithoutRef<"a"> & { children: ReactNode }) {
  const raw = href ?? "";
  const mdMatch = raw.match(/^([a-z0-9-]+)\.md(#.*)?$/i);
  if (mdMatch) {
    const to = `/docs/${mdMatch[1]}${mdMatch[2] ?? ""}`;
    return (
      <a
        href={to}
        className="font-medium text-accent-text underline-offset-2 hover:underline"
      >
        {children}
      </a>
    );
  }
  const isExternal = /^https?:\/\//i.test(raw);
  return (
    <a
      href={raw}
      {...(isExternal ? { target: "_blank", rel: "noopener noreferrer" } : {})}
      className="font-medium text-accent-text underline-offset-2 hover:underline"
    >
      {children}
    </a>
  );
}
